import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo';
import { iconUsers } from '@sorare/core/src/atoms/icons/system/iconUsers.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  BrandM,
  BrandXXL,
  HeadlineS,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge/index.tsx';
import { BackButton } from '@sorare/core/src/components/dialog/BackButton';
import { HideSeriesBottomNav } from '@sorare/core/src/components/navigation/AppNavigation/HideSeriesBottomNav';
import { ANY_SPORT_SERIES_SQUAD_CREATE_OR_JOIN } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSafePreviousNavigate } from '@sorare/core/src/hooks/useSafePreviousNavigate';

import setIcon from '#src/assets/set.svg';
import squadImg from '#src/assets/squad.webp';
import { SquadBackground } from '#src/components/squad/SquadBackground/index.tsx';
import { SquadCorners } from '#src/components/squad/SquadCorners/index.tsx';
import { SquadInviteBlock } from '#src/components/squad/SquadInviteBlock/index.tsx';
import { useJoinSquad } from '#src/hooks/squad/useJoinSquad.ts';

import { SquadContentBlock, SquadFooter } from '../ui';
import type {
  SquadJoinLookupQuery,
  SquadJoinLookupQueryVariables,
} from './__generated__/index.graphql.ts';

const CODE_LENGTH = 6;

const TITLE_GRADIENT = 'linear-gradient(180deg, #aa83ff 18%, #7d41ff 100%)';

const Root = styled(Vertical)`
  position: relative;
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
`;

const JoinedRoot = styled(Root)`
  background: linear-gradient(
    to bottom,
    transparent 0%,
    var(--c-blue-400) 300%
  );
`;

const BottomGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70%;
  background: linear-gradient(to bottom, transparent 0%, var(--c-black) 40%);
  pointer-events: none;
`;

const Content = styled(Vertical)`
  position: relative;
  padding: var(--triple-unit) 0;
`;

const CodeInputContainer = styled(Horizontal)`
  cursor: pointer;
`;

const Cell = styled(Horizontal).attrs({ gap: 0, center: true })`
  flex: 1;
  background: var(--c-nd-50);
  border-radius: var(--unit);
  padding: 10px var(--double-unit);
  --border-color: transparent;
  &.active {
    --border-color: var(--c-white);
  }
  border: 1px solid var(--border-color);
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
`;

const PreviewCard = styled(Vertical)`
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  align-items: center;
  overflow: hidden;
`;

const SquadLogo = styled.img`
  width: 56px;
  object-fit: contain;
`;

const SuccessContent = styled(Vertical)`
  position: relative;
  z-index: 1;
  flex: 1;
  align-items: center;
  padding: var(--quadruple-unit) var(--double-unit);
`;

const SuccessSquadLogo = styled.img`
  object-fit: contain;
  max-height: 12vh;
`;

type SquadData = NonNullable<SquadJoinLookupQuery['cardSet']>['squad'];

const SQUAD_JOIN_LOOKUP_QUERY = gql`
  query SquadJoinLookupQuery($joinSecret: String!) {
    cardSet {
      id
      squad(joinSecret: $joinSecret) {
        id
        name
        membersCount
        maxMembersCount
        joinSecret
      }
    }
  }
` as TypedDocumentNode<SquadJoinLookupQuery, SquadJoinLookupQueryVariables>;

export const SquadJoin = () => {
  const [code, setCode] = useState('');
  const [joined, setJoined] = useState(false);
  const [squadData, setSquadData] = useState<SquadData | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { generateSportContextPath } = useSportContext();
  const safeBack = useSafePreviousNavigate(
    generateSportContextPath(ANY_SPORT_SERIES_SQUAD_CREATE_OR_JOIN)
  );
  const { joinSquad, loading: joining } = useJoinSquad();

  const [lookupSquad, { loading: looking, error: lookupError }] = useLazyQuery(
    SQUAD_JOIN_LOOKUP_QUERY
  );

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, CODE_LENGTH);
    setCode(val);

    if (val.length < CODE_LENGTH) {
      setSquadData(null);
      return;
    }

    lookupSquad({ variables: { joinSecret: val } }).then(result => {
      if (result.data?.cardSet?.squad) {
        setSquadData(result.data.cardSet.squad);
      }
    });
  };

  const handleJoin = async () => {
    if (!squadData) return;

    const result = await joinSquad(code);
    if (result?.joinSquad?.currentUser) {
      setJoined(true);
    }
  };

  const isCodeComplete = code.length === CODE_LENGTH;
  const hasError = isCodeComplete && !looking && lookupError;

  if (joined && squadData) {
    return (
      <JoinedRoot>
        <SquadCorners topOnly />
        <SquadContentBlock>
          <SuccessContent gap={3}>
            <Horizontal gap={0.5} center>
              <SorareLogo size="small" />
              <img src={setIcon} alt="Set" />
            </Horizontal>
            <BrandXXL
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': TITLE_GRADIENT,
                } as CSSProperties
              }
            >
              <FormattedMessage defaultMessage="Squad ready" />
            </BrandXXL>
            <BodyM color="var(--c-white)" className="text-center">
              <FormattedMessage defaultMessage="Team up now for exclusive rewards" />
            </BodyM>
            <Vertical gap={2} center>
              <SuccessSquadLogo
                src={squadImg}
                alt=""
                width={135}
                height={135}
              />
              <BrandM>{squadData.name}</BrandM>
            </Vertical>
          </SuccessContent>
        </SquadContentBlock>
        <SquadFooter>
          <SquadInviteBlock
            joinSecret={squadData.joinSecret}
            membersCount={squadData.membersCount}
            maxMembersCount={squadData.maxMembersCount}
          />
        </SquadFooter>
        <HideSeriesBottomNav />
      </JoinedRoot>
    );
  }

  return (
    <Root>
      <SquadBackground />
      <BottomGradient />
      <SquadContentBlock>
        <Content gap={3}>
          <Vertical gap={1}>
            <BackButton onBack={safeBack} />
            <BrandM>
              <FormattedMessage defaultMessage="Join a Squad" />
            </BrandM>
            <BodyM color="var(--c-nd-700)">
              <FormattedMessage defaultMessage="Enter your Squad code" />
            </BodyM>
          </Vertical>
          <CodeInputContainer gap={1} onClick={() => inputRef.current?.focus()}>
            <HiddenInput
              type="text"
              ref={inputRef}
              value={code}
              onChange={handleCodeChange}
              maxLength={CODE_LENGTH}
              autoFocus
              name="squad-join-code"
            />
            {Array.from({ length: CODE_LENGTH }).map((_, i) => (
              <Cell
                key={i}
                className={classNames({
                  active:
                    i === code.length ||
                    (code.length === CODE_LENGTH && i === CODE_LENGTH - 1),
                })}
              >
                <BodyM color="var(--c-nd-600)" bold>
                  {code[i] || '-'}
                </BodyM>
              </Cell>
            ))}
          </CodeInputContainer>
          {!!hasError && (
            <BodyS color="var(--c-red-400)">
              <FormattedMessage defaultMessage="Squad not found. Check your code and try again." />
            </BodyS>
          )}
          {!!squadData && (
            <NeonEdge
              color="var(--c-inventory-green)"
              spread="40%"
              showNeon="always"
              showHalo="always"
            >
              <PreviewCard gap={1.5}>
                <SquadLogo src={squadImg} alt="" />
                <HeadlineS color="var(--c-white)">{squadData.name}</HeadlineS>
                <Horizontal gap={0.5} center>
                  <FontAwesomeIcon
                    icon={iconUsers}
                    size="xs"
                    color="var(--c-nd-600)"
                  />
                  <LabelS color="var(--c-nd-600)">
                    {squadData.membersCount}/{squadData.maxMembersCount}
                  </LabelS>
                </Horizontal>
              </PreviewCard>
            </NeonEdge>
          )}
        </Content>
      </SquadContentBlock>
      <SquadFooter>
        <Button
          size="large"
          color={squadData ? 'primary' : 'secondary'}
          onClick={handleJoin}
          disabled={!squadData || joining}
          fullWidth
        >
          <FormattedMessage defaultMessage="Join Squad" />
        </Button>
      </SquadFooter>
      <HideSeriesBottomNav />
    </Root>
  );
};
